/**
 * Čtení exportu Google Takeout (Firemní profil na Googlu) v prohlížeči (vize F2.1, F2.2,
 * F2.5, F2.8). Z jednoho nebo víc ZIPů, tak jak je Google stáhl, přečte jen centrální
 * adresáře a položky `location-*` s daty lokace a stránkami recenzí; fotky ani nic jiného.
 *
 * Výsledek už je v minimálním tvaru pro API: kvalifikovaná recenze (pět hvězd, původní
 * text, jméno a platný čas; `googleReviews.ts`) nese jméno, text a čas, ostatní jen ID,
 * hvězdy a příznak textu. Jména a texty ostatních ani odpověď CK (`reviewReply`) výsledek
 * nikdy nenese, takže neopustí prohlížeč. Nepoužitelný soubor vrací pojmenovanou chybu,
 * ne výjimku (chyby I/O samotného souboru propadnou).
 *
 * Bez zod (admin nenatahuje schémata) a bez barelu: importy z konkrétních souborů.
 */
import {
  GOOGLE_REVIEW_AUTHOR_MAX,
  GOOGLE_REVIEW_ID_PATTERN,
  GOOGLE_REVIEW_TEXT_MAX,
  isGoogleReviewTime,
  type StarCount,
  type TakeoutLocationRef,
  type TakeoutReviewPayload,
} from "../googleReviews";
import { exportDateFromFileName, localDayIso } from "./exportDate";
import { originalReviewText, TRANSLATION_MARKERS } from "./originalReviewText";
import { readZipDirectory, readZipEntry, ZipReadError } from "./zipDirectory";
import { compareCzech } from "../utils/czechSort";

export type TakeoutReadError = "not_zip" | "not_takeout" | "unsupported_zip" | "no_reviews";

export interface TakeoutLocation extends TakeoutLocationRef {
  reviews: TakeoutReviewPayload[];
}

export type TakeoutReadResult =
  | {
      ok: true;
      exportDate: string;
      exportDateSource: "filename" | "upload";
      locations: TakeoutLocation[];
    }
  | { ok: false; error: TakeoutReadError; fileName: string };

export interface TakeoutPathKind {
  kind: "reviews" | "location-data";
  locationKey: string;
}

/**
 * Jen ASCII konec cesty: název nadřazené složky je lokalizovaný a kódování jmen se mezi
 * zapisovači liší. `location-` musí následovat lomítko v další úrovni, jinak by se za lokaci
 * vydával `data.json` účtu nebo správce lokace (`locationAdmin-*`).
 */
const TAKEOUT_PATH = /(?:^|\/)location-([^/]+)\/(reviews(?:-[^/]+)?\.json|data\.json)$/;

export function classifyTakeoutPath(path: string): TakeoutPathKind | null {
  const m = TAKEOUT_PATH.exec(path);
  if (!m) return null;
  return { kind: m[2] === "data.json" ? "location-data" : "reviews", locationKey: m[1] };
}

const STARS: Record<string, StarCount> = { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 };

/** ID recenze = poslední segment `name` za segmentem `reviews` (`accounts/<a>/locations/<l>/reviews/<id>`). */
function reviewId(name: unknown): string | null {
  if (typeof name !== "string") return null;
  const segments = name.split("/");
  if (segments.at(-2) !== "reviews") return null;
  const id = segments.at(-1) ?? "";
  return GOOGLE_REVIEW_ID_PATTERN.test(id) ? id : null;
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

/**
 * Surová recenze exportu → tvar pro API, nebo `null` (neznámé hvězdy, bez ID, ID mimo vzor;
 * taková se nepočítá nikam). Výsledek se skládá výčtem polí, nikdy rozprostřením surového
 * objektu: `reviewReply` ani `reviewer` do něj nesmějí projít.
 */
export function parseReview(raw: unknown): TakeoutReviewPayload | null {
  const r = asRecord(raw);
  if (!r) return null;
  const id = reviewId(r.name);
  const stars = typeof r.starRating === "string" ? STARS[r.starRating] : undefined;
  if (id === null || stars === undefined) return null;

  const text = originalReviewText(typeof r.comment === "string" ? r.comment : "").trim();
  const textOk =
    text !== "" &&
    text.length <= GOOGLE_REVIEW_TEXT_MAX &&
    !TRANSLATION_MARKERS.some((m) => text.includes(m));
  const displayName = asRecord(r.reviewer)?.displayName;
  const author = typeof displayName === "string" ? displayName.trim() : "";
  const authorOk = author.length >= 1 && author.length <= GOOGLE_REVIEW_AUTHOR_MAX;
  const createTime = isGoogleReviewTime(r.createTime)
    ? (r.createTime as string)
    : isGoogleReviewTime(r.updateTime)
      ? (r.updateTime as string)
      : undefined;

  if (stars === 5 && textOk && authorOk && createTime !== undefined) {
    return { id, stars, hasText: true, author, text, createTime };
  }
  // Pětka, která kvalifikovaná není, jde bez textu: zod žádá jméno, text a čas právě u `5 && hasText`.
  return { id, stars, hasText: stars < 5 && textOk };
}

interface LocationAcc {
  name: string | null;
  placeId: string | null;
  reviews: Map<string, { review: TakeoutReviewPayload; updatedMs: number | null }>;
}

const NAME_FIELDS = ["title", "locationName", "name"] as const;

/** Jméno lokace z `data.json`: první neprázdné z `title`, `locationName`, `name`, které není jménem zdroje API. */
function locationName(data: Record<string, unknown>): string | null {
  for (const field of NAME_FIELDS) {
    const value = data[field];
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    if (trimmed !== "" && !trimmed.startsWith("accounts/") && !trimmed.startsWith("locations/"))
      return trimmed;
  }
  return null;
}

class NotTakeout extends Error {}

function parseJson(bytes: Uint8Array<ArrayBuffer>): unknown {
  try {
    // TextDecoder odstraní BOM.
    return JSON.parse(new TextDecoder("utf-8").decode(bytes));
  } catch {
    throw new NotTakeout();
  }
}

const updatedMs = (raw: unknown): number | null => {
  const value = asRecord(raw)?.updateTime;
  return isGoogleReviewTime(value) ? Date.parse(value as string) : null;
};

/** `data.json` lokace: jméno a Place ID (neprázdné hodnoty přepisují dřívější). */
function mergeLocationData(loc: LocationAcc, json: unknown): void {
  const data = asRecord(json);
  if (!data) return;
  loc.name = locationName(data) ?? loc.name;
  const placeId = asRecord(data.metadata)?.placeId;
  if (typeof placeId === "string" && placeId.trim() !== "") loc.placeId = placeId.trim();
}

/** Stránka `reviews*.json`: recenze podle ID, při duplicitě vyhrává pozdější `updateTime`. */
function mergeReviews(loc: LocationAcc, json: unknown): void {
  const list = asRecord(json)?.reviews;
  if (!Array.isArray(list)) return;
  for (const raw of list) {
    const review = parseReview(raw);
    if (!review) continue;
    const updated = updatedMs(raw);
    const previous = loc.reviews.get(review.id);
    // Při shodě, chybějícím nebo neplatném updateTime zůstává první nalezená.
    const newer = updated !== null && previous?.updatedMs != null && updated > previous.updatedMs;
    if (previous && !newer) continue;
    loc.reviews.set(review.id, { review, updatedMs: updated });
  }
}

/**
 * Přečte export (díly v daném pořadí, každý zvlášť) a vrátí lokace s recenzemi, nebo
 * pojmenovanou chybu s názvem souboru. Lokace se slučují přes soubory i díly podle klíče,
 * recenze podle ID (při duplicitě vyhrává pozdější `updateTime`).
 */
export async function readTakeoutExport(
  files: readonly File[],
  options: { today?: string } = {},
): Promise<TakeoutReadResult> {
  if (files.length === 0) return { ok: false, error: "not_takeout", fileName: "" };
  const locations = new Map<string, LocationAcc>();
  const acc = (key: string): LocationAcc => {
    let loc = locations.get(key);
    if (!loc) {
      loc = { name: null, placeId: null, reviews: new Map() };
      locations.set(key, loc);
    }
    return loc;
  };
  let firstWithReviews: string | null = null;

  for (const file of files) {
    try {
      const entries = await readZipDirectory(file);
      // Postupně, ne Promise.all: paměť a deterministické pořadí.
      for (const entry of entries) {
        const kind = classifyTakeoutPath(entry.name);
        if (!kind) continue;
        const json = parseJson(await readZipEntry(file, entry));
        const loc = acc(kind.locationKey);
        if (kind.kind === "location-data") {
          mergeLocationData(loc, json);
        } else {
          firstWithReviews ??= file.name;
          mergeReviews(loc, json);
        }
      }
    } catch (e) {
      if (e instanceof ZipReadError) return { ok: false, error: e.kind, fileName: file.name };
      if (e instanceof NotTakeout) return { ok: false, error: "not_takeout", fileName: file.name };
      throw e;
    }
  }

  if (firstWithReviews === null)
    return { ok: false, error: "not_takeout", fileName: files[0].name };
  // Lokace bez jediné platné recenze se nenabízí: její potvrzení by smazalo všechny exportované.
  const result: TakeoutLocation[] = [...locations]
    .filter(([, loc]) => loc.reviews.size > 0)
    .map(([locationKey, loc]) => ({
      locationKey,
      name: loc.name ?? `Lokace ${locationKey}`,
      placeId: loc.placeId,
      reviews: [...loc.reviews.values()]
        .map((x) => x.review)
        .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)),
    }))
    .sort((a, b) => compareCzech(a.name, b.name) || (a.locationKey < b.locationKey ? -1 : 1));
  if (result.length === 0) return { ok: false, error: "no_reviews", fileName: firstWithReviews };

  const stamped = files
    .map((f) => exportDateFromFileName(f.name))
    .filter((d): d is string => d !== null);
  const exportDate = stamped.length > 0 ? stamped.reduce((a, b) => (b > a ? b : a)) : null;
  return {
    ok: true,
    exportDate: exportDate ?? options.today ?? localDayIso(new Date()),
    exportDateSource: exportDate ? "filename" : "upload",
    locations: result,
  };
}
